import React, {useState} from 'react';
import Typography from '@mui/material/Typography';
import {Box, Button, Card, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Menu, MenuItem, Stack, styled, useMediaQuery, useTheme} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import PropTypes from 'prop-types';
import blueGrey from '@mui/material/colors/blueGrey';
import FileTypeIcon from '../../../../shared-components/FileTypeIcon';
import DocumentPreviewDialog from '../../../../shared-components/DocumentPreviewDialog';
import useUser from '../../../../utility/hooks/useUser';
import {simsStatus} from './documentSimsStatus';

// Maria, 17 October 2026: a tidier list, one row per document with its type icon, its name in bold, and 'View',
// which opens the document in the preview pop-up where it can be downloaded. Staff also see 'Delete'.
// A section that cannot give the file for a preview (no onFetch) keeps its download icon.

const DocumentsCountSpan = styled('span')(({theme}) => ({
    backgroundColor: blueGrey[50],
    color: blueGrey[700],
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    borderRadius: theme.spacing(1),
    fontWeight: 600,
    fontSize: 12
}));

const SMALL_BUTTON = {minWidth: 0, px: 1.5, py: 0.25, fontSize: 13, whiteSpace: 'nowrap'};

// AD-2753 (David, 22 October 2026): on a phone the row itself opens the document, there are no 'View' and 'Delete'
// buttons, 'Delete' is in the three-dot menu, and the name is shown in full over as many lines as it needs.
// Tablet and desktop are unchanged.
const DocumentList = ({documents, readonly, staffProfile}) => {
    const {user} = useUser();
    const theme = useTheme();
    const isPhone = useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
    // only members of staff and School Admins see which documents are available for parents
    // A staff profile's documents never go to parents, so the label is left off there.
    const isParentViewer = staffProfile || !user?.hasElevatedAccess?.();
    const [preview, setPreview] = useState({open: false, name: '', blob: null, loading: false});
    // AD-2639 (Maria, 20 October 2026): staff open a document's three-dot menu to change its settings or send it to SIMS
    const [menu, setMenu] = useState({anchor: null, section: null, document: null});
    const [settings, setSettings] = useState({open: false, section: null, document: null, isForParent: false, syncToSims: false});
    const closeMenu = () => setMenu({anchor: null, section: null, document: null});
    const canDelete = section => section.allowDelete && !readonly;
    // AD-2789: on a staff profile, a School Admin opens the menu of a document that can go to the SIMS staff record
    const staffSims = (section, document) => staffProfile && !readonly && !!section.onSyncToSims &&
        (!!document?.canSyncToSims || !!simsStatus(document));
    const hasMenu = (section, document) => (!readonly && !isParentViewer && (section.onEditSettings || section.onSyncToSims)) ||
        staffSims(section, document) || (isPhone && canDelete(section));
    const opensOnTap = (section, document) => isPhone && !!document.id && !!section.onFetch;

    // on a phone the whole row opens the document, by tap or with the keyboard
    const tapToOpen = (section, document) => {
        if (!opensOnTap(section, document)) return {};
        return {
            role: 'button',
            tabIndex: 0,
            'aria-label': `Open ${document.name}`,
            onClick: () => view(section, document),
            onKeyDown: event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    view(section, document);
                }
            }
        };
    };

    const view = async (section, document) => {
        setPreview({open: true, name: document.name, blob: null, loading: true});
        const response = await section.onFetch(document.id);
        const blob = response?.blob
            ? new Blob([response.blob], {type: response.mimeType || response.blob.type || 'application/octet-stream'})
            : null;
        setPreview({open: true, name: response?.filename || document.name, blob, loading: false});
    };

    return (
        <Stack flexDirection="column" gap={3}>
            {documents.map(section => (section.documents.length > 0
                ? (
                    <Box key={`section_${section.sectionName}`}>
                        {section.sectionName && (
                            <Stack direction="row" alignItems="center" gap={1} sx={{mb: 1}}>
                                <Typography variant="subtitle1" fontWeight={600}>{section.sectionName}</Typography>
                                <DocumentsCountSpan>{section.documents.length}</DocumentsCountSpan>
                            </Stack>
                        )}
                        <Card variant="outlined" sx={{borderRadius: 2, maxHeight: 480, overflowY: 'auto'}} onScroll={section.paginationHandler}>
                            {section.documents.map((document, index) => (
                                <Stack
                                    key={`${section.sectionName}_${index}`}
                                    direction="row"
                                    alignItems="center"
                                    gap={2}
                                    data-testid="document-row"
                                    {...tapToOpen(section, document)}
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderTop: index === 0 ? 'none' : '1px solid #eef0f3',
                                        '&:hover': {backgroundColor: '#f7f9fc'},
                                        ...(opensOnTap(section, document) ? {cursor: 'pointer'} : {})
                                    }}
                                >
                                    <FileTypeIcon name={document.name} sx={{fontSize: 30}}/>
                                    <Box sx={{flex: 1, minWidth: 0}}>
                                        <Typography fontWeight={600} sx={isPhone
                                            ? {whiteSpace: 'normal', overflowWrap: 'anywhere'}
                                            : {overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                            {document.name}
                                        </Typography>
                                        {/* Maria, 18 October 2026: 'Available for parent' is for staff only, so a parent is
                                            never told that some documents are not shown to them */}
                                        {/* AD-2639: staff also see whether the document has gone to SIMS */}
                                        {(() => {
                                            const notes = [
                                                document.summary,
                                                document.isForParent && !isParentViewer ? 'Available for parent' : '',
                                                section.onSyncToSims && (!isParentViewer || staffProfile) ? simsStatus(document) : ''
                                            ].filter(Boolean);
                                            return notes.length > 0 && (
                                                <Typography variant="body2" color="text.secondary">{notes.join(' · ')}</Typography>
                                            );
                                        })()}
                                    </Box>
                                    {!isPhone && document.id && section.onFetch && (
                                        <Button variant="outlined" size="small" sx={SMALL_BUTTON} onClick={() => view(section, document)}>
                                            View
                                        </Button>
                                    )}
                                    {document.id && !section.onFetch && section.onDownload && (
                                        <IconButton aria-label="Download" onClick={async () => await section.onDownload(document.id)}>
                                            <FileDownloadRoundedIcon fontSize="small"/>
                                        </IconButton>
                                    )}
                                    {!isPhone && canDelete(section) && (
                                        <Button variant="outlined" size="small" color="error" sx={SMALL_BUTTON} onClick={() => section.onDelete(document)}>
                                            Delete
                                        </Button>
                                    )}
                                    {document.id && hasMenu(section, document) && (
                                        <IconButton
                                            size="small"
                                            aria-label={`More actions for ${document.name}`}
                                            data-testid="document-menu"
                                            onClick={event => {
                                                // a tap on the three dots opens the menu, not the document
                                                event.stopPropagation();
                                                setMenu({anchor: event.currentTarget, section, document});
                                            }}
                                            onKeyDown={event => event.stopPropagation()}
                                        >
                                            <MoreVertIcon fontSize="small"/>
                                        </IconButton>
                                    )}
                                </Stack>
                            ))}
                        </Card>
                    </Box>
                )
                : null))}
            <Menu anchorEl={menu.anchor} open={!!menu.anchor} onClose={closeMenu}>
                {(menu.section?.onEditSettings || (menu.section && staffSims(menu.section, menu.document))) && (
                    <MenuItem onClick={() => {
                        setSettings({
                            open: true,
                            section: menu.section,
                            document: menu.document,
                            isForParent: !!menu.document.isForParent,
                            syncToSims: !!simsStatus(menu.document)
                        });
                        closeMenu();
                    }}>
                        Edit settings
                    </MenuItem>
                )}
                {isPhone && menu.section && canDelete(menu.section) && (
                    <MenuItem
                        sx={{color: 'error.main'}}
                        onClick={() => {
                            const {section, document} = menu;
                            closeMenu();
                            section.onDelete(document);
                        }}
                    >
                        Delete
                    </MenuItem>
                )}
            </Menu>
            <Dialog open={settings.open} onClose={() => setSettings(current => ({...current, open: false}))} maxWidth="xs" fullWidth>
                <DialogTitle sx={{pb: 0.5}}>Edit settings</DialogTitle>
                <DialogContent>
                    <Typography fontWeight={600} sx={{mb: 1, overflowWrap: 'anywhere'}}>{settings.document?.name}</Typography>
                    {/* a staff profile's documents never go to parents */}
                    {!staffProfile && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={settings.isForParent}
                                    onChange={event => setSettings(current => ({...current, isForParent: event.target.checked}))}
                                />
                            }
                            label="Available for parent"
                        />
                    )}
                    {/* Maria, 20 October 2026: a school can decide at any time to send a document to SIMS, however it was
                        added. Once sent it cannot be taken back out of SIMS, so the tick stays on. */}
                    {settings.section?.onSyncToSims && (
                        <FormControlLabel
                            sx={{display: 'flex'}}
                            control={
                                <Checkbox
                                    checked={settings.syncToSims}
                                    disabled={!!simsStatus(settings.document)}
                                    onChange={event => setSettings(current => ({...current, syncToSims: event.target.checked}))}
                                />
                            }
                            label={simsStatus(settings.document)
                                ? `Sync to SIMS Linked Documents (${simsStatus(settings.document)})`
                                : 'Sync to SIMS Linked Documents'}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSettings(current => ({...current, open: false}))}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (settings.section.onEditSettings && settings.isForParent !== !!settings.document.isForParent) {
                                await settings.section.onEditSettings(settings.document, settings.isForParent);
                            }
                            if (settings.syncToSims && !simsStatus(settings.document)) {
                                await settings.section.onSyncToSims(settings.document);
                            }
                            setSettings(current => ({...current, open: false}));
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <DocumentPreviewDialog
                open={preview.open}
                onClose={() => setPreview({open: false, name: '', blob: null, loading: false})}
                document={preview.blob}
                documentName={preview.name}
                isLoading={preview.loading}
            />
        </Stack>
    );
};

DocumentList.propTypes = {
    documents: PropTypes.array,
    readonly: PropTypes.bool,
    staffProfile: PropTypes.bool,
    disablePaddings: PropTypes.bool
};

export default DocumentList;
